import React, { useContext, useEffect, useRef, useState } from 'react'
import { Button } from '@mui/material'
import FormattedPrice from '@components/FormattedPrice'
import { Trans, useTranslation } from 'react-i18next'
import { OutOfStockStatusEnum } from '../../../../generated/gqlTypes'
import ProductFormFixedButtons from './ProductFormFixedButtons'
import { Element } from 'react-scroll'
import ProductContext from '../context/ProductContext'

const SCROLL_BUTTONS_ID = 'product-form-buttons'

interface Props {
  checkVariantAvailability(): boolean

  selectedVariant: {
    shippingRequired: boolean
    isInCart: boolean
    isSupply: boolean
    isCustomizable: boolean
    hasDefaultKit: boolean
    stockQuantity: number
    stockStatus: OutOfStockStatusEnum | null
    priceRange: {
      currency: string
      discount: {
        max: number
      }
    }
    defaultKitPrice: {
      currency: string
      gross: number
    } | null
  }

  onAddToBag(): Promise<void>

  onAddToBagFullKit(): Promise<void>

  onCustomizeKit(): Promise<void>

  isArchive: boolean

  onDownloadFiles(): void

  isInPack: boolean
  zipFileLink: string
}

const ProductFormButtons: React.FC<Props> = ({
  checkVariantAvailability,
  selectedVariant,
  onAddToBag,
  onAddToBagFullKit,
  onCustomizeKit,
  isArchive,
  onDownloadFiles,
  isInPack,
  zipFileLink,
}) => {
  const { t } = useTranslation()
  const { formValues } = useContext(ProductContext)
  // For kits this is the number of kits, so the button shows the price for all of them.
  const quantity = formValues.quantity || 1
  const ref = useRef(null)
  const [isVisible, setIsVisible] = useState(false)
  const [width, setWidth] = useState(0)

  useEffect(() => {
    if (!ref.current) return

    const observer = new ResizeObserver(([entry]) => {
      setWidth(entry.contentRect.width)
    })

    observer.observe(ref.current)

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.1 } // 10% visible
    )

    if (ref.current) observer.observe(ref.current)
    return () => observer.disconnect()
  }, [])

  const renderButtons = () => {
    if (!selectedVariant) {
      return (
        <div>
          <Button variant='contained' color='secondary' size='large' onClick={onAddToBag} fullWidth disabled>
            {t('button.addToBag', 'Add to bag')}
          </Button>
        </div>
      )
    }
    if (isInPack && !selectedVariant?.shippingRequired) {
      return (
        <div>
          {zipFileLink && (
            <Button
              variant='contained'
              color='secondary'
              size='large'
              onClick={onDownloadFiles}
              // href={zipFileLink}
              // download
              fullWidth
            >
              {t('button.download', 'Download')}
            </Button>
          )}
          {!zipFileLink && (
            <Button variant='contained' color='secondary' size='large' onClick={onDownloadFiles} fullWidth>
              {t('button.contactUsForMoreAttempts', 'Contact us for more attempts')}
            </Button>
          )}
        </div>
      )
    }
    const inBagDigital = selectedVariant.isInCart && !selectedVariant.shippingRequired
    const isSupply = selectedVariant.isSupply
    const isAvailable = checkVariantAvailability()
    return (
      <div>
        <div>
          {!isSupply && (
            <div>
              <div>
                <Button
                  variant='contained'
                  color='secondary'
                  size='large'
                  onClick={onAddToBag}
                  disabled={!isAvailable || inBagDigital || isArchive}
                  disableElevation
                  fullWidth
                >
                  {isArchive && t('button.archive', 'Archive')}
                  {!isArchive
                    ? inBagDigital
                      ? t('button.alreadyInTheBag', 'Already in the bag')
                      : t('button.addToBag', 'Add to bag')
                    : ''}
                </Button>
              </div>
            </div>
          )}
          {isSupply && (
            <>
              {!selectedVariant.isCustomizable && (
                <Button
                  variant='contained'
                  color='secondary'
                  size='large'
                  onClick={onAddToBagFullKit}
                  disabled={!isAvailable || isArchive}
                  disableElevation
                  fullWidth
                >
                  <Trans
                    i18nKey='button.addToBagFullKitPrice'
                    defaults='Add to bag <price/>'
                    components={{
                      price: (
                        <FormattedPrice
                          currency={selectedVariant.priceRange.currency}
                          amount={selectedVariant.priceRange.discount.max * quantity}
                        />
                      ),
                    }}
                  />
                </Button>
              )}
              {selectedVariant.isCustomizable && (
                <>
                  {selectedVariant.hasDefaultKit && (
                    <>
                      <Button
                        variant='contained'
                        color='secondary'
                        size='large'
                        onClick={onAddToBagFullKit}
                        disabled={!isAvailable || isArchive}
                        disableElevation
                        fullWidth
                      >
                        {selectedVariant.defaultKitPrice ? (
                          <Trans
                            i18nKey='button.addDefaultKitToCartPrice'
                            defaults='Add Default KIT to Cart — <price/>'
                            components={{
                              price: (
                                <FormattedPrice
                                  currency={selectedVariant.defaultKitPrice.currency}
                                  amount={selectedVariant.defaultKitPrice.gross * quantity}
                                />
                              ),
                            }}
                          />
                        ) : (
                          t('button.addDefaultKitToCart', 'Add Default KIT to Cart')
                        )}
                      </Button>
                      <div className='my-5' />
                    </>
                  )}
                  <Button
                    variant={selectedVariant.hasDefaultKit ? 'outlined' : 'contained'}
                    color='secondary'
                    size='large'
                    onClick={onCustomizeKit}
                    disabled={!isAvailable || isArchive}
                    disableElevation={!selectedVariant.hasDefaultKit}
                    fullWidth
                  >
                    {t('button.customizeTheKit', 'Customize the KIT')}
                  </Button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <Element name={SCROLL_BUTTONS_ID}>
      <div ref={ref}>
        {renderButtons()}
        {!isVisible && <ProductFormFixedButtons width={width} scrollId={SCROLL_BUTTONS_ID} />}
      </div>
    </Element>
  )
}

export default ProductFormButtons
